import { clampPrintFontSize } from 'helpers/printFontSize'
import { clampLogoScale } from 'helpers/printLogoSize'
import { parsePrintSequenceMode, type PrintSequenceMode } from 'helpers/printSequencePolicy'
import {
 DEFAULT_SHIFT_CLOSE_SUMMARY_TYPE,
 normalizeShiftCloseSummaryType,
 type ShiftCloseSummaryType,
} from 'helpers/shiftCloseSummaryType'
import { pendingOrderFingerprint } from 'utils/posCartFingerprint'
import { normalizeMaxPosManualDiscountPercent } from './posManualDiscountCap'
import localforage from 'localforage'

const idb = localforage.createInstance({
 name: 'erp-pos',
 storeName: 'pos',
})

/**
 * Every pending/failed POS order mutation is a read-modify-write operation.
 * Serialize those operations in-process and, where available, across browser
 * tabs/windows through the Web Locks API. Without this lock an auto-sync
 * removal could overwrite a checkout appended a moment earlier.
 */
const POS_ORDER_QUEUE_LOCK_NAME = 'erp-pos:offline-order-queue:v1'
const POS_ORDER_QUEUE_LEASE_KEY = 'erp_pos_offline_order_queue_lock_v1'
const POS_ORDER_QUEUE_LEASE_MS = 15_000
const POS_ORDER_QUEUE_LOCK_TIMEOUT_MS = 30_000
let posOrderQueueTail: Promise<void> = Promise.resolve()

async function withInProcessPosOrderQueueLock<T>(callback: () => Promise<T>): Promise<T> {
 const previous = posOrderQueueTail
 let release!: () => void
 const current = new Promise<void>((resolve) => {
  release = resolve
 })
 posOrderQueueTail = previous.then(() => current)
 await previous
 try {
  return await callback()
 } finally {
  release()
 }
}

function queueLockToken(): string {
 if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
  return crypto.randomUUID()
 }
 return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function readQueueLease(): { owner: string; expiresAt: number } | null {
 try {
  const raw = localStorage.getItem(POS_ORDER_QUEUE_LEASE_KEY)
  if (!raw) return null
  const parsed = JSON.parse(raw) as { owner?: unknown; expiresAt?: unknown }
  if (typeof parsed.owner !== 'string' || typeof parsed.expiresAt !== 'number') return null
  return { owner: parsed.owner, expiresAt: parsed.expiresAt }
 } catch {
  return null
 }
}

async function withStorageLeasePosOrderQueueLock<T>(callback: () => Promise<T>): Promise<T> {
 if (typeof localStorage === 'undefined') return callback()
 const owner = queueLockToken()
 const startedAt = Date.now()

 while (true) {
  const now = Date.now()
  const current = readQueueLease()
  if (!current || current.expiresAt <= now) {
   try {
    localStorage.setItem(
     POS_ORDER_QUEUE_LEASE_KEY,
     JSON.stringify({ owner, expiresAt: now + POS_ORDER_QUEUE_LEASE_MS }),
    )
   } catch {
    return callback()
   }
   if (readQueueLease()?.owner === owner) break
  }
  if (now - startedAt >= POS_ORDER_QUEUE_LOCK_TIMEOUT_MS) {
   throw new Error('Timed out acquiring the durable POS order queue lock')
  }
  await new Promise<void>((resolve) => setTimeout(resolve, 25 + Math.floor(Math.random() * 25)))
 }

 const renew = setInterval(() => {
  const current = readQueueLease()
  if (current?.owner !== owner) return
  try {
   localStorage.setItem(
    POS_ORDER_QUEUE_LEASE_KEY,
    JSON.stringify({ owner, expiresAt: Date.now() + POS_ORDER_QUEUE_LEASE_MS }),
   )
  } catch {
   /* The short IndexedDB mutation remains protected by the acquired lease. */
  }
 }, Math.floor(POS_ORDER_QUEUE_LEASE_MS / 3))

 try {
  return await callback()
 } finally {
  clearInterval(renew)
  if (readQueueLease()?.owner === owner) {
   try {
    localStorage.removeItem(POS_ORDER_QUEUE_LEASE_KEY)
   } catch {
    /* Expiry releases the lease if storage becomes unavailable. */
   }
  }
 }
}

async function withPosOrderQueueLock<T>(callback: () => Promise<T>): Promise<T> {
 return withInProcessPosOrderQueueLock(async () => {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (locks?.request) {
   return locks.request(POS_ORDER_QUEUE_LOCK_NAME, { mode: 'exclusive' }, callback)
  }
  return withStorageLeasePosOrderQueueLock(callback)
 })
}

export const POS_KEYS = {
 catalog: 'pos_catalog',
 syncMeta: 'pos_sync_meta',
 pendingOrders: 'pos_pending_orders',
 failedOrders: 'pos_failed_orders',
 tableCarts: 'pos_table_carts',
 locallyOccupiedTables: 'pos_locally_occupied_tables',
 /** آخر رقم طلب مُخصَّص على الجهاز لكل فرع (للطباعة بدون إنترنت) */
 printSequenceByBranch: 'pos_print_sequence_by_branch',
 printSequenceStartByScope: 'pos_print_sequence_start_by_scope',
 activeShift: 'pos_active_shift',
} as const

export interface OfflineCacheScope {
 tenantId?: string | null
 branchId?: string | null
 userId?: string | number | null
}

const UNKNOWN_SCOPE = 'unknown'

function isKnownOfflineScopePart(value: string | number | null | undefined): boolean {
 const normalized = value == null ? '' : String(value).trim()
 return normalized !== '' && normalized !== UNKNOWN_SCOPE
}

function pendingOrderMatchesScope(
 order: Pick<PendingOfflineOrder, 'tenant_id' | 'branch_id'>,
 scope: OfflineCacheScope,
): boolean {
 if (
  isKnownOfflineScopePart(scope.tenantId)
  && order.tenant_id
  && String(order.tenant_id) !== String(scope.tenantId)
 ) return false
 if (
  isKnownOfflineScopePart(scope.branchId)
  && order.branch_id
  && String(order.branch_id) !== String(scope.branchId)
 ) return false
 return true
}

function canMigrateLegacyOrderScope(scope: OfflineCacheScope): boolean {
 return isKnownOfflineScopePart(scope.tenantId) && isKnownOfflineScopePart(scope.branchId)
}

function scopePart(value: string | number | null | undefined): string {
 const raw = value == null ? '' : String(value).trim()
 return encodeURIComponent(raw || UNKNOWN_SCOPE)
}

function readAuthScope(): OfflineCacheScope {
 const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('authUser') : null
 let parsed: any = null
 if (raw) {
 try {
 parsed = JSON.parse(raw)
 } catch {
 parsed = null
 }
 }

 const user = parsed?.user ?? parsed
 const tenantId =
 parsed?.tenant_slug ??
 (typeof localStorage !== 'undefined' ? localStorage.getItem('tenant_slug') : null) ??
 user?.tenant_id ??
 user?.company_id ??
 null
 const branchId =
 (typeof localStorage !== 'undefined' ? localStorage.getItem('selected_branch_id') : null) ??
 user?.current_branch_id ??
 null

 return {
 tenantId,
 branchId,
 userId: user?.id ?? null,
 }
}

export function resolveOfflineScope(overrides: OfflineCacheScope = {}): Required<OfflineCacheScope> {
 const base = readAuthScope()
 return {
 tenantId: overrides.tenantId ?? base.tenantId ?? UNKNOWN_SCOPE,
 branchId: overrides.branchId ?? base.branchId ?? UNKNOWN_SCOPE,
 userId: overrides.userId ?? base.userId ?? UNKNOWN_SCOPE,
 }
}

export function scopedPosKey(baseKey: string, scope: OfflineCacheScope = {}, mode: 'branch' | 'userBranch' = 'branch'): string {
 const resolved = resolveOfflineScope(scope)
 const parts = [baseKey, scopePart(resolved.tenantId)]
 if (mode === 'userBranch') parts.push(scopePart(resolved.userId))
 parts.push(scopePart(resolved.branchId))
 return parts.join(':')
}

function catalogMatchesBranch(payload: PosCatalogPayload | null, branchId: string | null | undefined): boolean {
 if (!payload) return false
 if (!branchId || branchId === UNKNOWN_SCOPE) return true
 return String(payload.branch_id ?? '') === String(branchId)
}

function activeShiftMatchesScope(shift: unknown, scope: OfflineCacheScope): boolean {
 if (!shift || typeof shift !== 'object') return true
 const rec = shift as { branch_id?: string | null }
 const branchId = scope.branchId
 if (branchId && branchId !== UNKNOWN_SCOPE && rec.branch_id && String(rec.branch_id) !== String(branchId)) {
 return false
 }
 return true
}

export interface PosSyncMeta {
 lastPullAt: string | null
 version: number
 branchId: string | null
}

export interface PosCatalogVariant {
 id: string
 name?: string
 sku?: string | null
 attributes?: Record<string, string> | null
 additional_price?: string | number | null
}

export interface PosCatalogUnit {
 id: number
 name: string
 factor_to_base: string | number
 is_base: boolean
 barcode?: string | null
}

export type PosOptionPricingType = 'free' | 'per_option' | 'group_price'
export type PosOptionSelectionType = 'single' | 'multiple'

export interface PosCatalogOption {
 id: number
 name: string
 price: string | number
 sort_order?: number
 is_active?: boolean
}

export interface PosCatalogOptionGroup {
 id: number
 title: string
 selection_type: PosOptionSelectionType
 pricing_type: PosOptionPricingType
 price_behavior?: 'additive' | 'defines_base_price'
 group_price?: string | number | null
 is_required: boolean
 min_selections?: number | null
 max_selections?: number | null
 sort_order?: number
 is_active?: boolean
 options: PosCatalogOption[]
}

export type InventoryMode = 'stock_product' | 'recipe_product' | 'non_stock'

export interface PosCatalogRecipe {
  id: number
  variant_id?: string | null
  modifier_option_id?: number | null
  ingredient_product_id: number
  quantity: string | number
  unit_id: number
  waste_percentage?: string | number | null
  warehouse_id?: string | null
  is_active?: boolean
}

export interface RecipeIngredientProduct {
  name: string
  units: Array<{ id: number; factor_to_base: number }>
}

export interface PosCatalogProduct {
 id: number
 name: string
 barcode?: string | null
 barcodes?: string[] | null
 unit?: string | null
 units?: PosCatalogUnit[]
 selling_price: string | number
 promotional_price?: string | number | null
 is_promotional?: boolean
 promotional_start_date?: string | null
 promotional_end_date?: string | null
 /** @deprecated POS must use branch_available_quantity — not global stock */
 stock_quantity: number
 /** Sellable quantity in current branch default warehouse (matches sale validation). */
 branch_available_quantity?: number
 /** Alias of branch_available_quantity from POS sync API. */
 available_quantity?: number
 warehouse_id?: string | null
 warehouse_name?: string | null
 stock_scope_label?: string | null
 reserved_quantity?: string | number | null
 min_stock_alert?: number
 tax_rate?: string | number | null
 category_id?: number | null
 sort_order?: number
 category?: { id: number; name: string } | null
 brand?: string | null
 cost_price?: string | number | null
 variants?: PosCatalogVariant[]
 image?: string | null
 inventory_mode?: InventoryMode
 /** افتراضياً متتبع — false = بيع بدون قيود مخزون في الـ POS */
 track_inventory?: boolean
 recipes?: PosCatalogRecipe[]
 option_groups?: PosCatalogOptionGroup[]
 kitchen_routing_type?: 'screen' | 'printer' | 'none' | null
 kitchen_station_id?: string | null
 kitchen_printer_id?: string | null
}

export interface PosCatalogCategory {
 id: number
 name: string
 image?: string | null
 sort_order?: number
 parent_id?: number | null
 kitchen_routing_type?: 'screen' | 'printer' | 'none' | null
 kitchen_station_id?: string | null
 kitchen_printer_id?: string | null
}

export type KitchenRouteType = 'screen' | 'printer' | 'none'

export interface PosCatalogKitchenPrinter {
 id: string
 name: string
 printer_name: string
 is_active: boolean
}

export interface PosCatalogCustomer {
 id: number
 name: string
 phone?: string | null
 points_balance?: number
 balance?: string | number
 debt?: string | number
}

export interface PosCatalogVault {
 id: string
 name: string
 branch_id: string
 is_active: boolean
 account_type?: string
 payment_types?: PaymentLineMethod[]
 default_payment_types?: PaymentLineMethod[]
 allow_sales?: boolean
 allow_refunds?: boolean
 allow_expenses?: boolean
 account_id?: string | null
}

export interface PosCatalogFinancialAccount {
 id: string
 legacy_vault_id?: string | null
 name: string
 account_type: string
 payment_method: PaymentLineMethod
 provider_name?: string | null
 masked_identifier?: string | null
 branch_scope: 'selected_branches' | 'all_branches'
 is_default: boolean
 is_active: boolean
 allow_sales: boolean
 allow_refunds: boolean
 currency: string
}

export interface PosCatalogWarehouse {
 id: string
 name: string
 branch?: { id: string; name?: string; code?: string } | null
 status: string
}

export interface PosCatalogCoupon {
 id: string
 code: string
 name: string
 description?: string | null
 type: 'percentage' | 'fixed'
 value: number
 min_order_amount?: number | null
 max_discount_amount?: number | null
 max_uses?: number | null
 max_uses_per_customer?: number | null
 used_count: number
 starts_at?: string | null
 expires_at?: string | null
 is_active: boolean
 branch_id?: string | null
}

export type AppliedCoupon = {
 coupon: PosCatalogCoupon
 discount: number
}

/** Payload returned from GET /sync/pos-data (inner `data`). */
export interface PosCatalogPayload {
 generated_at: string
 version: number
 price_defining_options_version?: number
 branch_id: string
 branch?: {
 id: string
 name: string
 code?: string
 address?: string
 phone?: string
 email?: string
 settings?: Record<string, unknown> | null
 }
   products: PosCatalogProduct[]
   /** {ingredient_product_id: available_qty} — for offline recipe advisory check (R3-H1). */
   recipe_ingredient_balances?: Record<string, number>
   /** {ingredient_product_id: {name, units[]}} — for offline recipe name + unit factor (C5). */
   recipe_ingredient_products?: Record<string, RecipeIngredientProduct>
   categories: PosCatalogCategory[]
 customers: PosCatalogCustomer[]
 promotions: unknown[]
 coupons: PosCatalogCoupon[]
 /** Cached from sync when delivery zones API is used offline */
 delivery_zones?: Array<{ id: string; name: string; delivery_fee: string | number; branch_id?: string; is_active?: boolean }>
 vaults: PosCatalogVault[]
 financial_accounts?: PosCatalogFinancialAccount[]
 warehouses: PosCatalogWarehouse[]
 settings: Record<string, unknown>
 /** Open shift for current user/branch from sync (includes vault_id for POS cash routing). */
 open_shift?: {
 id?: string
 vault_id?: string
 opened_at?: string
 starting_cash?: string | number
 drawer_ledger_enabled?: boolean
 accounting_model?: 'shift_drawer_ledger' | 'legacy_vault_shift'
 expected_cash?: string | number
 } | null
 kitchen_printers?: PosCatalogKitchenPrinter[]
 pagination?: {
 products?: {
 total: number
 per_page: number
 current_page: number
 last_page: number
 } | null
 customers?: {
 limit: number
 count: number
 } | null
 }
}

export type BranchPrintSettingsNormalized = {
 auto_print_receipt: boolean
 enable_kitchen_print: boolean
 allow_unpaid_takeaway_kitchen_print: boolean
 use_server_kitchen_print_queue: boolean
 receipt_show_invoice_number: boolean
 receipt_show_invoice_barcode: boolean
 receipt_show_product_category: boolean
 receipt_show_print_sequence: boolean
 receipt_show_branch_name: boolean
 customer_printer_name: string
 cash_drawer_printer_name: string
 /** Max kitchen/print ticket sequence per branch; empty = no wrap */
 print_sequence_max: string
 print_sequence_mode: PrintSequenceMode
 receipt_footer: string
 customer_receipt_footer_message: string
 customer_receipt_developer_footer: string
 receipt_logo_url: string
 receipt_header: string
 receipt_address: string
 receipt_phone: string
 receipt_email: string
 receipt_return_policy: string
  print_shift_close_report: boolean
  shift_close_summary_type: ShiftCloseSummaryType
  customer_receipt_font_size: number
  kitchen_ticket_font_size: number
  shift_close_font_size: number
  customer_receipt_logo_scale: number
}

export const DEFAULT_CUSTOMER_RECEIPT_FOOTER_MESSAGE = 'شكراً لشرائك!'
export const DEFAULT_CUSTOMER_RECEIPT_DEVELOPER_FOOTER =
 'تم التطوير بواسطة مدار - 01055566412'

/** Normalizes `branches.settings` / `catalog.branch.settings` for POS printing (snake_case). */
export function normalizeBranchPrintSettings(
 raw: Record<string, unknown> | null | undefined,
): BranchPrintSettingsNormalized {
 const r = raw ?? {}
 const hasSetting = (key: string): boolean => Object.prototype.hasOwnProperty.call(r, key)
 const stringSetting = (key: string, fallback = ''): string => {
 if (!hasSetting(key)) return fallback
 const value = r[key]
 if (value == null) return ''
 return typeof value === 'string' ? value : String(value)
 }
 const boolSetting = (key: string, defaultValue: boolean): boolean => {
 const value = r[key]
 if (value === undefined || value === null || value === '') return defaultValue
 return value === true || value === 1 || value === '1'
 }
 const legacyReceiptFooter = stringSetting('receipt_footer')
 const customerFooterFallback = hasSetting('receipt_footer')
 ? legacyReceiptFooter
 : DEFAULT_CUSTOMER_RECEIPT_FOOTER_MESSAGE

 return {
 auto_print_receipt: boolSetting('auto_print_receipt', false),
 enable_kitchen_print: boolSetting('enable_kitchen_print', false),
 allow_unpaid_takeaway_kitchen_print: (() => {
  const kitchenOn = boolSetting('enable_kitchen_print', false)
  if (!kitchenOn) return false
  const value = r.allow_unpaid_takeaway_kitchen_print
  if (value === true || value === 1 || value === '1') return true
  if (typeof value === 'string') {
   const normalized = value.trim().toLowerCase()
   return normalized === 'true' || normalized === 'on' || normalized === 'yes'
  }
  return false
 })(),
 use_server_kitchen_print_queue: boolSetting('use_server_kitchen_print_queue', false),
 receipt_show_invoice_number: boolSetting('receipt_show_invoice_number', true),
 receipt_show_invoice_barcode: boolSetting('receipt_show_invoice_barcode', true),
 receipt_show_product_category: boolSetting('receipt_show_product_category', false),
 receipt_show_print_sequence: boolSetting('receipt_show_print_sequence', true),
 receipt_show_branch_name: boolSetting('receipt_show_branch_name', true),
 customer_printer_name:
 typeof r.customer_printer_name === 'string' ? r.customer_printer_name : '',
 cash_drawer_printer_name:
 typeof r.cash_drawer_printer_name === 'string' ? r.cash_drawer_printer_name : '',
 print_sequence_max:
 r.print_sequence_max != null && r.print_sequence_max !== ''
 ? String(r.print_sequence_max)
 : '',
 print_sequence_mode: parsePrintSequenceMode(r.print_sequence_mode),
 receipt_footer: legacyReceiptFooter,
 customer_receipt_footer_message: stringSetting(
 'customer_receipt_footer_message',
 customerFooterFallback,
 ),
 customer_receipt_developer_footer: stringSetting(
 'customer_receipt_developer_footer',
 DEFAULT_CUSTOMER_RECEIPT_DEVELOPER_FOOTER,
 ),
 receipt_logo_url: typeof r.receipt_logo_url === 'string' ? r.receipt_logo_url : '',
 receipt_header: typeof r.receipt_header === 'string' ? r.receipt_header : '',
 receipt_address: typeof r.receipt_address === 'string' ? r.receipt_address : '',
 receipt_phone: typeof r.receipt_phone === 'string' ? r.receipt_phone : '',
 receipt_email: typeof r.receipt_email === 'string' ? r.receipt_email : '',
 receipt_return_policy: typeof r.receipt_return_policy === 'string' ? r.receipt_return_policy : '',
 print_shift_close_report: boolSetting('print_shift_close_report', false),
 shift_close_summary_type: hasSetting('shift_close_summary_type')
  ? normalizeShiftCloseSummaryType(r.shift_close_summary_type)
  : DEFAULT_SHIFT_CLOSE_SUMMARY_TYPE,
  customer_receipt_font_size: clampPrintFontSize(r.customer_receipt_font_size, 'customer'),
  kitchen_ticket_font_size: clampPrintFontSize(r.kitchen_ticket_font_size, 'kitchen'),
  shift_close_font_size: clampPrintFontSize(r.shift_close_font_size, 'customer'),
  customer_receipt_logo_scale: clampLogoScale(r.customer_receipt_logo_scale),
  }
}

/** POS cart: manual discount & coupon toggles from branch.settings (default enabled). */
export type BranchPosCartSettingsNormalized = {
 allow_pos_discount: boolean
 allow_pos_coupon: boolean
 /** null = unlimited. 0 = no manual discount. Never coerce missing to 0. */
 max_pos_manual_discount_percent: number | null
}

export function normalizeBranchPosCartSettings(
 raw: Record<string, unknown> | null | undefined,
): BranchPosCartSettingsNormalized {
 const r = raw ?? {}
 const boolDefaultTrue = (key: string): boolean => {
 const value = r[key]
 if (value === undefined || value === null || value === '') return true
 if (value === false || value === 0 || value === '0') return false
 return value === true || value === 1 || value === '1'
 }
 return {
 allow_pos_discount: boolDefaultTrue('allow_pos_discount'),
 allow_pos_coupon: boolDefaultTrue('allow_pos_coupon'),
 max_pos_manual_discount_percent: normalizeMaxPosManualDiscountPercent(r.max_pos_manual_discount_percent),
 }
}

export type CartLineSelectedOption = {
 product_option_group_id: number
 group_title: string
 pricing_type: PosOptionPricingType
 price_behavior?: 'additive' | 'defines_base_price'
 group_price?: number
 options: Array<{
 product_option_id: number
 name: string
 option_price: number
 applied_price: number
 }>
}

export type CartLine = {
 /** Unique cart line key. Same product/variant/unit + same options merge by quantity. */
 line_uid?: string
 product_id: number
 product_name: string
 category_name?: string | null
 quantity: number
 unit_price: number
 /** سعر المنتج قبل إضافة الخيارات (للسطر الحالي). يُستخدم عرضياً وللتجزئة. */
 base_unit_price?: number
 discount: number
 /** Product tax used when the branch-wide tax setting is unset. */
 tax_rate?: number
 unit_id?: number | null
 unit_name?: string | null
 unit_factor?: number | null
 variant_id?: string | null
 variant_name?: string | null
 selected_options?: CartLineSelectedOption[]
 kitchen_route_type?: KitchenRouteType | null
 kitchen_printer_id?: string | null
 kitchen_station_id?: string | null
 /** Cumulative qty already sent to kitchen printer for this line. */
 kitchen_printed_quantity?: number
 kitchen_print_last_at?: string | null
 kitchen_print_status?: 'not_printed' | 'printing' | 'printed' | 'failed'
 /** Item-level kitchen/prep note. Does not affect pricing. */
 notes?: string
}

export type TableCartSnapshot = {
 orderId?: number | string | null
 draftGeneration?: number | null
 /** Explicit user clear retained locally until the server accepts the empty draft. */
 cleared?: boolean
 lines: CartLine[]
 cartDiscount: number
 customer: any
 appliedCoupon?: AppliedCoupon | null
 orderNotes?: string
}

export type TableCartsRecord = Record<string, TableCartSnapshot>

const LEGACY_TABLE_CARTS_SESSION = 'pos_table_carts'

export async function getTableCartsRecord(): Promise<TableCartsRecord> {
 const raw = (await idb.getItem(POS_KEYS.tableCarts)) as TableCartsRecord | null
 if (raw && typeof raw === 'object') return raw
 const legacy = sessionStorage.getItem(LEGACY_TABLE_CARTS_SESSION)
 if (legacy) {
 try {
 const parsed = JSON.parse(legacy) as TableCartsRecord
 sessionStorage.removeItem(LEGACY_TABLE_CARTS_SESSION)
 await idb.setItem(POS_KEYS.tableCarts, parsed)
 return parsed
 } catch {
 /* ignore */
 }
 }
 return {}
}

export async function setTableCartsRecord(map: TableCartsRecord): Promise<void> {
 await idb.setItem(POS_KEYS.tableCarts, map)
}

export type PosPaymentType =
 | 'cash'
 | 'card'
 | 'credit'
 | 'layaway'
 | 'split'
 | 'wallet'
 | 'electronic_wallet'
 | 'instapay'
 | 'bank_transfer'
 | 'payment_gateway'

export type PaymentLineMethod =
 | 'cash'
 | 'card'
 | 'wallet'
 | 'electronic_wallet'
 | 'instapay'
 | 'bank_transfer'
 | 'payment_gateway'

export type PaymentLine = {
 financial_account_id?: string | null
 vault_id?: string | null
 amount: number
 method?: string
 payment_method?: PaymentLineMethod
 reference?: string | null
 client_line_id?: string
}

function normalizePaymentLine(raw: unknown): PaymentLine | null {
 if (!raw || typeof raw !== 'object') return null
 const rec = raw as Record<string, unknown>
 const amount = Number(rec.amount)
 if (!Number.isFinite(amount) || amount < 0) return null

 const rawMethod = rec.payment_method ?? rec.method
 const method = rawMethod == null ? '' : String(rawMethod).trim().toLowerCase()
 const financialAccountId =
 rec.financial_account_id == null || String(rec.financial_account_id).trim() === ''
 ? null
 : String(rec.financial_account_id)
 const reference =
 rec.reference == null || String(rec.reference).trim() === ''
 ? null
 : String(rec.reference).trim()
 const clientLineId =
 rec.client_line_id == null || String(rec.client_line_id).trim() === ''
 ? undefined
 : String(rec.client_line_id)

 const line: PaymentLine = {
  amount,
  ...(method ? { payment_method: method as PaymentLineMethod } : {}),
  ...(financialAccountId ? { financial_account_id: financialAccountId } : {}),
  ...(reference ? { reference } : {}),
  ...(clientLineId ? { client_line_id: clientLineId } : {}),
 }

 // A legacy vault id is valid routing information for cash only. Persisting it
 // on electronic lines can route money to the wrong ledger after reconnect.
 if (method === 'cash' && rec.vault_id != null && String(rec.vault_id).trim() !== '') {
  line.vault_id = String(rec.vault_id)
 }
 if (method === 'wallet') {
  delete line.financial_account_id
  delete line.reference
 }
 return line
}

/** Normalizes payment allocations without losing account, reference, or idempotency fields. */
export function normalizePaymentLines(raw: unknown): PaymentLine[] | null {
 const parsed = parseJsonIfString(raw)
 if (!Array.isArray(parsed)) return null
 const lines = parsed
  .map(normalizePaymentLine)
  .filter((line): line is PaymentLine => line != null)
 return lines.length > 0 ? lines : null
}

export type LayawayTerms = {
 base_total: number
 markup_percent: number
 term_months: number
 down_payment_amount: number
 first_due_date: string
}

export interface PendingOfflineOrder {
 client_uuid: string
 /** Stable cart/checkout identity. Equal to client_uuid for POS checkouts. */
 checkout_intent_id?: string
 /** Same semantic idempotency value as client_uuid; explicit for offline audit/retry tooling. */
 idempotency_key?: string
 /** Phase 8 offline payload schema version */
 offline_schema_version?: number
 price_defining_options_version?: number
 tenant_id?: string | null
 user_id?: string | number | null
 shift_id?: string | null
 pos_register_id?: string | null
 register_session_id?: string | null
 created_at_local?: string
 /** Stable POS installation and app build that created the checkout. */
 device_id?: string
 client_version?: string
 /** Immutable local receipt data used only for safe reprint after a crash. */
 client_print_snapshot?: Record<string, unknown>
 /** Timestamp of the current upload lease; never used as sale ownership time. */
 sync_started_at?: string | null
 status?: 'pending' | 'syncing' | 'synced' | 'failed' | 'conflict'
 retry_count?: number
 last_error?: string | null
 /** Structured sync conflict code from server */
 conflict_code?: string | null
 branch_id: string
 warehouse_id: string
 sale_date: string
 dining_table_id?: string | null
 order_type?: 'dine_in' | 'takeaway' | 'delivery'
 customer_id?: number | null
 customer_snapshot?: {
    name: string
    phone: string
    email?: string | null
    notes?: string | null
    additional_phones?: Array<{ phone: string; label?: string; notes?: string }>
 } | null
 items: Array<{
 product_id: number
 quantity: number
 unit_id?: number | null
 unit_price: number
 discount?: number
 variant_id?: string | null
 selected_options?: Array<{
 product_option_group_id: number
 option_ids: number[]
 }>
 client_cart_line_id?: string | null
 kitchen_route_type?: KitchenRouteType | null
 kitchen_printer_id?: string | null
 kitchen_station_id?: string | null
 kitchen_printed_quantity?: number
 kitchen_printed_at?: string | null
 }>
 subtotal: number
 tax: number
 discount: number
 total: number
 paid: number
 cash_tendered?: number
 payment_type: PosPaymentType
 loyalty_points_redeemed?: number
 loyalty_discount?: number
 notes?: string
 payment_lines?: PaymentLine[] | null
 layaway_terms?: LayawayTerms | null
 coupon_id?: string | null
 coupon_discount?: number
 delivery_fee?: number
 delivery_contract_version?: 'delivery_pricing_v1' | 'delivery_pricing_v2'
 delivery_fee_override_reason?: string | null
 service_charge?: number
 delivery_address?: string
 delivery_phone?: string
 delivery_zone_id?: string | null
 customer_address_id?: string | null
 delivery_collection_method?: 'cashier' | 'driver'
 /** Promotion portion of combined invoice discount (for server branch policy). */
 promotion_discount?: number
 /** رقم الطلب (مطبخ) — يُحسب على العميل للعمل أوفلاين؛ يُرسل للخادم عند المزامنة */
 print_sequence?: number
}

/** قديم: حقول متعددة لنفس البيانات (سلة / طلب محفوظ) */
type LooseOfflineOrder = PendingOfflineOrder & {
 lines?: unknown
 cart_items?: unknown
 order_items?: unknown
 sale_items?: unknown
 line_items?: unknown
 productId?: number
}

function asPlainObject(v: unknown): Record<string, unknown> | null {
 if (v != null && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>
 return null
}

function parseJsonIfString(v: unknown): unknown {
 if (typeof v !== 'string' || v.trim() === '') return v
 try {
 return JSON.parse(v) as unknown
 } catch {
 return v
 }
}

/** يحوّل سطر بند من واجهات مختلفة (snake_case / camelCase / qty) */
function normalizeLineItem(l: unknown): PendingOfflineOrder['items'][0] | null {
 const r = asPlainObject(l)
 if (!r) return null
 const product_id = Number(r.product_id ?? r.productId)
 const quantity = Math.max(1, Math.floor(Number(r.quantity ?? r.qty ?? 0) || 0))
 const unit_id = r.unit_id != null ? Number(r.unit_id) : null
 const unit_price = Number(r.unit_price ?? r.unitPrice ?? 0)
 const discount = r.discount != null ? Number(r.discount) : 0
 const variant_id = (r.variant_id ?? r.variantId ?? null) as string | null
 if (!Number.isFinite(product_id) || product_id <= 0) return null
 if (unit_id != null && (!Number.isFinite(unit_id) || unit_id <= 0)) return null
 if (!Number.isFinite(unit_price) || unit_price < 0) return null

 const rawOptions = parseJsonIfString(r.selected_options ?? r.selectedOptions)
 let selected_options: Array<{ product_option_group_id: number; option_ids: number[] }> | undefined
 if (Array.isArray(rawOptions)) {
 selected_options = rawOptions
 .map((g) => {
 const grec = asPlainObject(g)
 if (!grec) return null
 const groupId = Number(grec.product_option_group_id ?? grec.group_id ?? grec.id)
 const ids = Array.isArray(grec.option_ids)
 ? grec.option_ids
 : Array.isArray(grec.options)
 ? (grec.options as unknown[])
 .map((o) => {
 const orec = asPlainObject(o)
 if (!orec) return null
 const oid = Number(orec.product_option_id ?? orec.id)
 return Number.isFinite(oid) && oid > 0 ? oid : null
 })
 .filter((x): x is number => x !== null)
 : []
 const optionIds = (ids as unknown[])
 .map((x) => Number(x))
 .filter((x) => Number.isFinite(x) && x > 0)
 if (!Number.isFinite(groupId) || groupId <= 0 || optionIds.length === 0) return null
 return { product_option_group_id: groupId, option_ids: optionIds }
 })
 .filter((x): x is { product_option_group_id: number; option_ids: number[] } => x !== null)
 if (selected_options.length === 0) selected_options = undefined
 }

 const clientCartLineId =
 typeof r.client_cart_line_id === 'string' && r.client_cart_line_id.trim() !== ''
 ? r.client_cart_line_id.trim()
 : typeof r.line_uid === 'string' && r.line_uid.trim() !== ''
 ? r.line_uid.trim()
 : undefined
 const kitchenPrintedQty = r.kitchen_printed_quantity != null ? Number(r.kitchen_printed_quantity) : undefined
 const kitchenPrintedAt =
 typeof r.kitchen_printed_at === 'string' && r.kitchen_printed_at.trim() !== ''
 ? r.kitchen_printed_at
 : typeof r.kitchen_print_last_at === 'string' && r.kitchen_print_last_at.trim() !== ''
 ? r.kitchen_print_last_at
 : undefined
 const rawKitchenRouteType = r.kitchen_route_type ?? r.kitchenRouteType
 const kitchenRouteType =
 rawKitchenRouteType === 'screen' || rawKitchenRouteType === 'printer' || rawKitchenRouteType === 'none'
 ? rawKitchenRouteType
 : undefined
 const rawKitchenPrinterId = r.kitchen_printer_id ?? r.kitchenPrinterId
 const kitchenPrinterId =
 typeof rawKitchenPrinterId === 'string' && rawKitchenPrinterId.trim() !== ''
 ? rawKitchenPrinterId.trim()
 : null
 const rawKitchenStationId = r.kitchen_station_id ?? r.kitchenStationId
 const kitchenStationId =
 typeof rawKitchenStationId === 'string' && rawKitchenStationId.trim() !== ''
 ? rawKitchenStationId.trim()
 : null

 return {
 product_id,
 quantity,
 unit_id: unit_id != null ? unit_id : null,
 unit_price,
 discount: Number.isFinite(discount) ? discount : 0,
 variant_id: variant_id && String(variant_id).trim() !== '' ? String(variant_id) : null,
 selected_options,
 ...(clientCartLineId ? { client_cart_line_id: clientCartLineId } : {}),
 ...(kitchenRouteType
 ? {
 kitchen_route_type: kitchenRouteType,
 kitchen_printer_id: kitchenRouteType === 'printer' ? kitchenPrinterId : null,
 kitchen_station_id: kitchenRouteType === 'screen' ? kitchenStationId : null,
 }
 : {}),
 ...(kitchenPrintedQty != null && Number.isFinite(kitchenPrintedQty)
 ? { kitchen_printed_quantity: Math.max(0, Math.floor(kitchenPrintedQty)) }
 : {}),
 ...(kitchenPrintedAt ? { kitchen_printed_at: kitchenPrintedAt } : {}),
 }
}

/** يجمع أول مصفوفة بنود غير فارغة من مفاتيح شائعة أو كائن يشبه القائمة */
function extractRawLineArrays(rec: Record<string, unknown>): unknown[] {
 const keys = ['items', 'lines', 'cart_items', 'order_items', 'sale_items', 'line_items'] as const
 for (const key of keys) {
 let v = rec[key]
 v = parseJsonIfString(v)
 if (Array.isArray(v) && v.length > 0) return v
 if (v != null && typeof v === 'object' && !Array.isArray(v)) {
 const vals = Object.values(v as Record<string, unknown>)
 if (vals.length > 0 && vals.every((x) => x != null && typeof x === 'object')) {
 return vals
 }
 }
 }
 return []
}

/**
 * يجهّز طلباً مخزّناً للمزامنة: يستخرج البنود من أي شكل مدعوم، ويرجع null إن لا توجد بنود صالحة.
 */
export function coercePendingOrderForSync(raw: unknown): PendingOfflineOrder | null {
 if (!raw || typeof raw !== 'object') return null
 const o = raw as LooseOfflineOrder
 if (typeof o.client_uuid !== 'string' || !o.client_uuid) return null

 const rec = raw as Record<string, unknown>
 const rawLines = extractRawLineArrays(rec)
 let items = rawLines.map(normalizeLineItem).filter((x): x is NonNullable<typeof x> => x != null)

 if (items.length === 0 && Array.isArray(o.items)) {
 items = o.items.map(normalizeLineItem).filter((x): x is NonNullable<typeof x> => x != null)
 }

 if (items.length === 0) return null

 return { ...o, items, payment_lines: normalizePaymentLines(o.payment_lines) }
}

function parseCatalogQuantity(value: unknown): number {
 const n = Number(value)
 if (!Number.isFinite(n)) return 0
 return Math.max(0, Math.floor(n))
}

export function normalizePosCatalogProduct(raw: PosCatalogProduct): PosCatalogProduct {
 const branchAvailable = parseCatalogQuantity(
 raw.branch_available_quantity ?? raw.available_quantity
 )

 return {
 ...raw,
 branch_available_quantity: branchAvailable,
 available_quantity: branchAvailable,
 stock_scope_label: raw.stock_scope_label ?? 'مخزن الفرع',
 }
}

export function normalizePosCatalogPayload(payload: PosCatalogPayload): PosCatalogPayload {
 return {
 ...payload,
 products: (payload.products ?? []).map((p) => normalizePosCatalogProduct(p)),
  financial_accounts: (payload.financial_accounts ?? []).map((raw) => {
   const account = raw as PosCatalogFinancialAccount & Record<string, unknown>
   return {
    id: String(account.id),
    legacy_vault_id: account.legacy_vault_id == null ? null : String(account.legacy_vault_id),
    name: String(account.name ?? ''),
    account_type: String(account.account_type ?? ''),
    payment_method: account.payment_method,
    provider_name: account.provider_name == null ? null : String(account.provider_name),
    masked_identifier:
    account.masked_identifier == null ? null : String(account.masked_identifier),
    branch_scope: account.branch_scope,
    is_default: Boolean(account.is_default),
    is_active: Boolean(account.is_active),
    allow_sales: Boolean(account.allow_sales),
    allow_refunds: Boolean(account.allow_refunds),
    currency: String(account.currency ?? ''),
   }
  }),
 }
}

export async function getCatalog(
 branchId?: string | null,
 scope: OfflineCacheScope = {}
): Promise<PosCatalogPayload | null> {
 const resolved = resolveOfflineScope({ ...scope, branchId: branchId ?? scope.branchId })
 const scoped = (await idb.getItem(scopedPosKey(POS_KEYS.catalog, resolved))) as PosCatalogPayload | null
 if (scoped && catalogMatchesBranch(scoped, resolved.branchId)) {
 return normalizePosCatalogPayload(scoped)
 }

 const legacy = (await idb.getItem(POS_KEYS.catalog)) as PosCatalogPayload | null
 return catalogMatchesBranch(legacy, resolved.branchId)
 ? normalizePosCatalogPayload(legacy as PosCatalogPayload)
 : null
}

export async function setCatalog(payload: PosCatalogPayload, scope: OfflineCacheScope = {}): Promise<void> {
 const normalized = normalizePosCatalogPayload(payload)
 const resolved = resolveOfflineScope({ ...scope, branchId: scope.branchId ?? normalized.branch_id })
 await idb.setItem(POS_KEYS.catalog, normalized)
 const meta: PosSyncMeta = {
 lastPullAt: new Date().toISOString(),
 version: normalized.version ?? 1,
 branchId: normalized.branch_id ?? null,
 }
 await idb.setItem(POS_KEYS.syncMeta, meta)
 await idb.setItem(scopedPosKey(POS_KEYS.catalog, resolved), normalized)
 await idb.setItem(scopedPosKey(POS_KEYS.syncMeta, resolved), meta)
}

/**
 * Optimistically reduce branch sellable qty after a committed sale so POS badges
 * match inventory_balances without waiting for the next full catalog pull.
 */
export async function applyLocalSaleStockDelta(
 lines: Array<{ product_id: number; quantity: number; unit_id?: number | null; unit_factor?: number | null }>,
 branchId?: string | null,
): Promise<void> {
 const catalog = await getCatalog(branchId)
 if (!catalog?.products?.length || !lines.length) return

 const byProduct = new Map<number, number>()
 for (const line of lines) {
  const product = catalog.products.find((p) => p.id === line.product_id)
  let factor = 1
  if (line.unit_factor != null && Number.isFinite(Number(line.unit_factor))) {
   const n = Number(line.unit_factor)
   if (n > 0) factor = n
  } else if (line.unit_id != null && product?.units?.length) {
   const u = product.units.find((x) => x.id === line.unit_id)
   const n = Number(u?.factor_to_base)
   if (Number.isFinite(n) && n > 0) factor = n
  }
  const baseQty = Math.max(0, Math.floor((Number(line.quantity) || 0) * factor))
  if (baseQty <= 0) continue
  byProduct.set(line.product_id, (byProduct.get(line.product_id) ?? 0) + baseQty)
 }
 if (byProduct.size === 0) return

 const nextProducts = catalog.products.map((p) => {
  const delta = byProduct.get(p.id)
  if (!delta) return p
  const current =
   p.branch_available_quantity != null
    ? Number(p.branch_available_quantity)
    : p.available_quantity != null
      ? Number(p.available_quantity)
      : 0
  const nextQty = Math.max(0, current - delta)
  return {
   ...p,
   branch_available_quantity: nextQty,
   available_quantity: nextQty,
  }
 })

 await setCatalog({ ...catalog, products: nextProducts }, { branchId: branchId ?? catalog.branch_id })
 if (typeof window !== 'undefined') {
  window.dispatchEvent(new CustomEvent('pos:catalog-updated'))
 }
}

export async function getSyncMeta(
 branchId?: string | null,
 scope: OfflineCacheScope = {}
): Promise<PosSyncMeta | null> {
 const resolved = resolveOfflineScope({ ...scope, branchId: branchId ?? scope.branchId })
 const scoped = (await idb.getItem(scopedPosKey(POS_KEYS.syncMeta, resolved))) as PosSyncMeta | null
 if (scoped && (!resolved.branchId || resolved.branchId === UNKNOWN_SCOPE || String(scoped.branchId ?? '') === String(resolved.branchId))) {
 return scoped
 }
 const legacy = (await idb.getItem(POS_KEYS.syncMeta)) as PosSyncMeta | null
 if (!legacy) return null
 if (resolved.branchId && resolved.branchId !== UNKNOWN_SCOPE && legacy.branchId && String(legacy.branchId) !== String(resolved.branchId)) {
 return null
 }
 return legacy
}

/**
 * يخصص رقم الطلب التالي للفرع ويخزّنه محلياً (نفس منطق التكرار عند الحد الأقصى).
 * يُستدعى قبل حفظ الطلب في قائمة الانتظار حتى تطبع الإيصال صحيحاً بدون إنترنت.
 */
export async function allocateNextPrintSequence(
 branchId: string,
 maxCapRaw: string,
 options?: { scopeKey?: string; start?: number },
): Promise<number> {
 const trimmed = maxCapRaw.trim()
 const maxCap =
 trimmed === ''
 ? null
 : (() => {
 const n = parseInt(trimmed, 10)
 return Number.isFinite(n) && n >= 1 ? n : null
 })()

 const mapKey = options?.scopeKey ?? branchId
 const key = POS_KEYS.printSequenceByBranch
 const map = ((await idb.getItem(key)) as Record<string, number> | null) ?? {}
 const startMap =
 ((await idb.getItem(POS_KEYS.printSequenceStartByScope)) as Record<string, number> | null) ?? {}
 const start =
 options?.start != null && options.start >= 1
 ? options.start
 : startMap[mapKey] != null && startMap[mapKey] >= 1
 ? startMap[mapKey]
 : null
 const last = map[mapKey] ?? (start != null ? start - 1 : 0)
 const next =
 start != null
 ? (maxCap != null && last + 1 > maxCap ? start : last + 1)
 : maxCap != null
 ? (last % maxCap) + 1
 : last + 1
 map[mapKey] = next
 await idb.setItem(key, map)
 return next
}

export async function resetPrintSequenceScope(scopeKey: string, start: number): Promise<void> {
 if (!scopeKey || start < 1) return
 const key = POS_KEYS.printSequenceByBranch
 const map = ((await idb.getItem(key)) as Record<string, number> | null) ?? {}
 map[scopeKey] = start - 1
 await idb.setItem(key, map)
 const startMap =
 ((await idb.getItem(POS_KEYS.printSequenceStartByScope)) as Record<string, number> | null) ?? {}
 startMap[scopeKey] = start
 await idb.setItem(POS_KEYS.printSequenceStartByScope, startMap)
}

export async function ensurePrintSequenceScope(
 scopeKey: string,
 start: number,
 last?: number | null,
): Promise<void> {
 if (!scopeKey) return
 const key = POS_KEYS.printSequenceByBranch
 const map = ((await idb.getItem(key)) as Record<string, number> | null) ?? {}
 if (map[scopeKey] == null) {
 map[scopeKey] = last != null && last >= 0 ? last : start - 1
 await idb.setItem(key, map)
 }
 const startMap =
 ((await idb.getItem(POS_KEYS.printSequenceStartByScope)) as Record<string, number> | null) ?? {}
 if (startMap[scopeKey] == null && start >= 1) {
 startMap[scopeKey] = start
 await idb.setItem(POS_KEYS.printSequenceStartByScope, startMap)
 }
}

export async function syncPrintSequenceScope(scopeKey: string, seq: number): Promise<void> {
 if (!scopeKey || seq < 1) return
 const key = POS_KEYS.printSequenceByBranch
 const map = ((await idb.getItem(key)) as Record<string, number> | null) ?? {}
 map[scopeKey] = seq
 await idb.setItem(key, map)
}

export const OFFLINE_SCHEMA_VERSION = 3

function normalizePendingOrders(orders: PendingOfflineOrder[]): PendingOfflineOrder[] {
 return orders.map((order) => ({
  ...order,
  offline_schema_version: order.offline_schema_version ?? OFFLINE_SCHEMA_VERSION,
  payment_lines: normalizePaymentLines(order.payment_lines),
 }))
}

async function writePendingOrdersUnlocked(
 orders: PendingOfflineOrder[],
 scope: OfflineCacheScope,
): Promise<void> {
 const scopedKey = scopedPosKey(POS_KEYS.pendingOrders, scope, 'branch')
 await idb.setItem(scopedKey, normalizePendingOrders(orders))
}

async function readPendingOrdersUnlocked(
 resolved: Required<OfflineCacheScope>,
 migrateLegacy = true,
): Promise<PendingOfflineOrder[]> {
 const scopedKey = scopedPosKey(POS_KEYS.pendingOrders, resolved, 'branch')
 const scoped = (await idb.getItem(scopedKey)) as PendingOfflineOrder[] | null
 const legacy = (await idb.getItem(POS_KEYS.pendingOrders)) as PendingOfflineOrder[] | null
 const merged = mergeOrderLists(scoped ?? [], legacy ?? [], resolved)
 const matchingLegacy = (legacy ?? []).filter((order) => pendingOrderMatchesScope(order, resolved))
 if (migrateLegacy && matchingLegacy.length > 0 && canMigrateLegacyOrderScope(resolved)) {
  // Move only this tenant/branch partition. Keeping every unmatched row in the
  // legacy key prevents opening branch A from deleting branch B's unsynced invoices.
  await writePendingOrdersUnlocked(merged, resolved)
  const remainingLegacy = (legacy ?? []).filter((order) => !pendingOrderMatchesScope(order, resolved))
  if (remainingLegacy.length > 0) {
   await idb.setItem(POS_KEYS.pendingOrders, remainingLegacy)
  } else {
   await idb.removeItem(POS_KEYS.pendingOrders)
  }
 }
 return normalizePendingOrders(merged)
}

export async function getPendingOrders(scope?: OfflineCacheScope): Promise<PendingOfflineOrder[]> {
 const resolved = resolveOfflineScope(scope)
 return withPosOrderQueueLock(() => readPendingOrdersUnlocked(resolved))
}

function mergeOrderLists(
 scoped: PendingOfflineOrder[],
 legacy: PendingOfflineOrder[],
 scope: OfflineCacheScope,
): PendingOfflineOrder[] {
 const byUuid = new Map<string, PendingOfflineOrder>()
 for (const order of scoped) byUuid.set(order.client_uuid, order)
 for (const order of legacy) {
  if (!pendingOrderMatchesScope(order, scope)) continue
  if (!byUuid.has(order.client_uuid)) byUuid.set(order.client_uuid, order)
 }
 return Array.from(byUuid.values())
}

export async function savePendingOrders(
 orders: PendingOfflineOrder[],
 scope?: OfflineCacheScope,
): Promise<void> {
 const resolved = resolveOfflineScope(scope)
 await withPosOrderQueueLock(() => writePendingOrdersUnlocked(orders, resolved))
}

/** Clear FA catalog / shift caches on logout; keep recoverable pending/failed orders. */
export async function clearSensitivePosCachesOnLogout(): Promise<void> {
 const resolved = readAuthScope()
 await idb.removeItem(POS_KEYS.catalog)
 await idb.removeItem(scopedPosKey(POS_KEYS.catalog, resolved))
 await idb.removeItem(POS_KEYS.syncMeta)
 await idb.removeItem(scopedPosKey(POS_KEYS.syncMeta, resolved))
 await idb.removeItem(POS_KEYS.activeShift)
 await idb.removeItem(scopedPosKey(POS_KEYS.activeShift, resolved, 'userBranch'))
}

function normalizeShiftId(raw: unknown): string | null {
 if (raw == null || String(raw).trim() === '') return null
 return String(raw)
}

function shiftRecordMatchesBranch(
 shift: { branch_id?: string | null } | null | undefined,
 branchId: string | null | undefined,
): boolean {
 if (!shift || !branchId || branchId === UNKNOWN_SCOPE) return true
 if (!shift.branch_id) return true
 return String(shift.branch_id) === String(branchId)
}

export interface ResolveShiftIdOptions {
 branchId?: string | null
 activeShift?: { id?: string | number; branch_id?: string | null } | null
 catalog?: PosCatalogPayload | null
 /** When false, skip online shiftsAPI.current fallback. Default true. */
 fetchOnline?: boolean
}

/** Single source of truth for offline sale shift attribution (checkout, persist, push). */
export async function resolveShiftIdForOfflineSale(
 opts: ResolveShiftIdOptions = {},
): Promise<string | null> {
 const branchId = opts.branchId ?? null

 const fromActive = normalizeShiftId(opts.activeShift?.id)
 if (fromActive && shiftRecordMatchesBranch(opts.activeShift, branchId)) {
 return fromActive
 }

 const fromCatalog = normalizeShiftId(opts.catalog?.open_shift?.id)
 if (fromCatalog) return fromCatalog

 const cached = await getCachedActiveShift<{ id?: string; branch_id?: string }>(branchId)
 const fromCache = normalizeShiftId(cached?.id)
 if (fromCache && shiftRecordMatchesBranch(cached, branchId)) {
 return fromCache
 }

 if (opts.fetchOnline !== false) {
 try {
 const { checkConnectivity } = await import('utils/connectivityCheck')
 if (await checkConnectivity()) {
 const { shiftsAPI } = await import('api/shifts')
 const { getUserData } = await import('helpers/Auth')
 const res = await shiftsAPI.current(branchId ?? undefined)
 if (res.status === 'success' && res.data?.id) {
 await setCachedActiveShift(res.data, {
 branchId: res.data.branch_id ?? branchId ?? undefined,
 userId: getUserData()?.id ?? undefined,
 })
 return String(res.data.id)
 }
 }
 } catch {
 /* offline or API error */
 }
 }

 return null
}

export async function patchPendingOrdersShiftIds(
 updates: Map<string, string>,
 scope?: OfflineCacheScope,
): Promise<void> {
 if (updates.size === 0) return
 const resolved = resolveOfflineScope(scope)
 await withPosOrderQueueLock(async () => {
  const list = await readPendingOrdersUnlocked(resolved)
  let changed = false
  const next = list.map((o) => {
   const id = updates.get(o.client_uuid)
   if (id && o.shift_id !== id) {
    changed = true
    return { ...o, shift_id: id }
   }
   return o
  })
  if (changed) await writePendingOrdersUnlocked(next, resolved)
 })
}

/**
 * Historical compatibility hook. Persisted orders must never acquire the
 * currently open Shift during load; the backend can resolve a legacy row only
 * from its original sale_date when that ownership is unambiguous.
 */
export async function migrateLegacyOfflineOrderShiftIds(
 _opts: ResolveShiftIdOptions = {},
): Promise<number> {
 return 0
}

export async function appendPendingOrder(
 order: PendingOfflineOrder,
 resolveOpts?: ResolveShiftIdOptions,
): Promise<PendingOfflineOrder> {
 const scope = resolveOfflineScope({ branchId: order.branch_id, tenantId: order.tenant_id, userId: order.user_id })
 let shiftId = normalizeShiftId(order.shift_id)
 if (!shiftId) {
 shiftId = await resolveShiftIdForOfflineSale({
 branchId: order.branch_id ?? scope.branchId,
 ...resolveOpts,
 })
 }
 const enriched: PendingOfflineOrder = {
 ...order,
 offline_schema_version: order.offline_schema_version ?? OFFLINE_SCHEMA_VERSION,
 idempotency_key: order.idempotency_key ?? order.client_uuid,
 tenant_id: order.tenant_id ?? scope.tenantId,
 user_id: order.user_id ?? scope.userId,
 shift_id: shiftId,
 created_at_local: order.created_at_local ?? new Date().toISOString(),
 status: order.status ?? 'pending',
 retry_count: order.retry_count ?? 0,
 last_error: order.last_error ?? null,
  payment_lines: normalizePaymentLines(order.payment_lines),
 }
 const snapshot =
 typeof structuredClone === 'function'
 ? structuredClone(enriched)
 : JSON.parse(JSON.stringify(enriched)) as PendingOfflineOrder
 return withPosOrderQueueLock(async () => {
  const list = await readPendingOrdersUnlocked(scope)
  const uuid = normalizeClientUuid(snapshot.client_uuid)
  const existing = list.find((candidate) => normalizeClientUuid(candidate.client_uuid) === uuid)
  if (existing) return existing

  await writePendingOrdersUnlocked([...list, snapshot], scope)

  // Prove durability while still holding the exact same queue lock and scope.
  // A separate read in the checkout screen can race with auto-sync, which may
  // legitimately upload and remove the row before that read starts.
  const stored = (await readPendingOrdersUnlocked(scope, false)).find(
   (candidate) => normalizeClientUuid(candidate.client_uuid) === uuid,
  )
  if (!stored) {
   const error = new Error('POS checkout was not readable after IndexedDB append')
   error.name = 'PendingOrderPersistenceError'
   throw error
  }
  return stored
 })
}

export async function removePendingByClientUuids(uuids: Set<string>): Promise<void> {
 if (uuids.size === 0) return
 const resolved = resolveOfflineScope()
 const removeSet = new Set(Array.from(uuids, normalizeClientUuid))
 await withPosOrderQueueLock(async () => {
  const list = await readPendingOrdersUnlocked(resolved)
  const next = list.filter((o) => !removeSet.has(normalizeClientUuid(o.client_uuid)))
  if (next.length !== list.length) await writePendingOrdersUnlocked(next, resolved)
 })
}

export async function markPendingOrderSyncing(clientUuid: string): Promise<boolean> {
 const resolved = resolveOfflineScope()
 return withPosOrderQueueLock(async () => {
  const list = await readPendingOrdersUnlocked(resolved)
  const normalized = normalizeClientUuid(clientUuid)
  const idx = list.findIndex((o) => normalizeClientUuid(o.client_uuid) === normalized)
  if (idx < 0) return false
  if (list[idx].status === 'syncing') return false
  list[idx] = { ...list[idx], status: 'syncing', sync_started_at: new Date().toISOString() }
  await writePendingOrdersUnlocked(list, resolved)
  return true
 })
}

export async function resetPendingOrderStatus(clientUuid: string, status: PendingOfflineOrder['status'] = 'pending'): Promise<void> {
 const resolved = resolveOfflineScope()
 await withPosOrderQueueLock(async () => {
  const list = await readPendingOrdersUnlocked(resolved)
  const normalized = normalizeClientUuid(clientUuid)
  const idx = list.findIndex((o) => normalizeClientUuid(o.client_uuid) === normalized)
  if (idx < 0) return
  list[idx] = { ...list[idx], status, sync_started_at: null }
  await writePendingOrdersUnlocked(list, resolved)
 })
}

/** Atomically re-queue conflicts without overwriting orders appended by checkout. */
export async function requeueConflictedPendingOrders(scope?: OfflineCacheScope): Promise<number> {
 const resolved = resolveOfflineScope(scope)
 return withPosOrderQueueLock(async () => {
  const list = await readPendingOrdersUnlocked(resolved)
  let changed = 0
  const next = list.map((order) => {
   if (order.status !== 'conflict') return order
   changed += 1
   return { ...order, status: 'pending' as const, retry_count: 0, last_error: null }
  })
  if (changed > 0) await writePendingOrdersUnlocked(next, resolved)
  return changed
 })
}

const SYNCING_STALE_MS = 5 * 60 * 1000

/** Pending or in-flight rows matching cart content, exposed for diagnostics only. */
export async function hasActivePendingWithFingerprint(
 fingerprint: string,
 branchId: string,
): Promise<boolean> {
 const list = await getPendingOrders({ branchId })
 const now = Date.now()
 return list.some((o) => {
 if (String(o.branch_id) !== String(branchId)) return false
 if (o.status !== 'pending' && o.status !== 'syncing') return false
 if (o.status === 'syncing' && o.sync_started_at) {
 const age = now - new Date(o.sync_started_at).getTime()
 if (age > SYNCING_STALE_MS) return false
 }
 return pendingOrderFingerprint(o) === fingerprint
 })
}

export interface FailedOfflineOrder {
 order: PendingOfflineOrder
 reason: string
 failedAt: string
 /** Last time a sync attempt recorded this failure (or duplicate rejection). */
 lastAttemptAt?: string
 retryCount: number
}

/** Patch a failed order payment line after authorized server-side account replacement. */
export async function updateFailedOrderPaymentAccount(
 clientUuid: string,
 clientLineId: string,
 replacementFinancialAccountId: string,
 scope?: OfflineCacheScope,
): Promise<boolean> {
 const resolved = resolveOfflineScope(scope)
 return withPosOrderQueueLock(async () => {
  const list = await readFailedOrdersUnlocked(resolved)
  const normalized = normalizeClientUuid(clientUuid)
  const idx = list.findIndex((f) => normalizeClientUuid(f.order.client_uuid) === normalized)
  if (idx < 0) return false
  const entry = list[idx]
  const lines = normalizePaymentLines(entry.order.payment_lines) ?? []
  let found = false
  const nextLines = lines.map((line) => {
   if (String(line.client_line_id ?? '') !== clientLineId) return line
   found = true
   return { ...line, financial_account_id: replacementFinancialAccountId }
  })
  if (!found) return false
  const next: FailedOfflineOrder = {
   ...entry,
   order: {
    ...entry.order,
    payment_lines: nextLines,
    conflict_code: null,
    last_error: null,
    status: 'failed',
   },
   reason: entry.reason,
  }
  const copy = [...list]
  copy[idx] = next
  await writeFailedOrdersUnlocked(copy, resolved)
  return true
 })
}

async function readFailedOrdersUnlocked(
 resolved: Required<OfflineCacheScope>,
 migrateLegacy = true,
): Promise<FailedOfflineOrder[]> {
 const scopedKey = scopedPosKey(POS_KEYS.failedOrders, resolved, 'branch')
 const scoped = (await idb.getItem(scopedKey)) as FailedOfflineOrder[] | null
 const legacy = (await idb.getItem(POS_KEYS.failedOrders)) as FailedOfflineOrder[] | null
 const byUuid = new Map<string, FailedOfflineOrder>()
 for (const row of scoped ?? []) byUuid.set(row.order.client_uuid, row)
 for (const row of legacy ?? []) {
  if (!pendingOrderMatchesScope(row.order, resolved)) continue
  if (!byUuid.has(row.order.client_uuid)) byUuid.set(row.order.client_uuid, row)
 }
 const merged = Array.from(byUuid.values())
 const matchingLegacy = (legacy ?? []).filter((row) => pendingOrderMatchesScope(row.order, resolved))
 if (migrateLegacy && matchingLegacy.length > 0 && canMigrateLegacyOrderScope(resolved)) {
  await writeFailedOrdersUnlocked(merged, resolved)
  const remainingLegacy = (legacy ?? []).filter((row) => !pendingOrderMatchesScope(row.order, resolved))
  if (remainingLegacy.length > 0) {
   await idb.setItem(POS_KEYS.failedOrders, remainingLegacy)
  } else {
   await idb.removeItem(POS_KEYS.failedOrders)
  }
 }
 return merged
}

async function writeFailedOrdersUnlocked(
 orders: FailedOfflineOrder[],
 resolved: Required<OfflineCacheScope>,
): Promise<void> {
 await idb.setItem(scopedPosKey(POS_KEYS.failedOrders, resolved, 'branch'), orders)
}

export async function getFailedOrders(scope?: OfflineCacheScope): Promise<FailedOfflineOrder[]> {
 const resolved = resolveOfflineScope(scope)
 return withPosOrderQueueLock(() => readFailedOrdersUnlocked(resolved))
}

/** Merge a failure into the list: one row per client_uuid, bumping retry metadata on repeats. Exported for unit tests. */
export function mergeFailedOrderIntoList(
 list: FailedOfflineOrder[],
 order: PendingOfflineOrder,
 reason: string,
): FailedOfflineOrder[] {
 const now = new Date().toISOString()
 const idx = list.findIndex((f) => f.order.client_uuid === order.client_uuid)
 if (idx >= 0) {
 const copy = [...list]
 copy[idx] = {
 ...copy[idx],
 order,
 reason,
 lastAttemptAt: now,
 retryCount: (copy[idx].retryCount ?? 0) + 1,
 }
 return copy
 }
 return [...list, { order, reason, failedAt: now, lastAttemptAt: now, retryCount: 1 }]
}

export async function appendFailedOrder(order: PendingOfflineOrder, reason: string, _retryCount: number): Promise<void> {
 const resolved = resolveOfflineScope({ branchId: order.branch_id, tenantId: order.tenant_id, userId: order.user_id })
 await withPosOrderQueueLock(async () => {
  const list = await readFailedOrdersUnlocked(resolved)
  await writeFailedOrdersUnlocked(mergeFailedOrderIntoList(list, order, reason), resolved)
 })
 if (typeof window !== 'undefined') {
 window.dispatchEvent(new CustomEvent('pos:failed-orders-updated'))
 }
}

/** Re-queue a single failed offline order for the normal push path. */
export async function moveFailedOrderToPending(clientUuid: string): Promise<{ ok: true } | { ok: false; error: string }> {
 const failed = await getFailedOrders()
 const entry = failed.find((f) => f.order.client_uuid === clientUuid)
 if (!entry) return { ok: false, error: 'not_found' }
 const pending = await getPendingOrders()
 if (pending.some((o) => o.client_uuid === clientUuid)) {
 return { ok: false, error: 'already_pending' }
 }
 await appendPendingOrder(entry.order)
 await removeFailedByClientUuids(new Set([clientUuid]))
 if (typeof window !== 'undefined') {
 window.dispatchEvent(new CustomEvent('pos:failed-orders-updated'))
 }
 return { ok: true }
}

/** Re-queue every failed offline order (caller usually runs push + bootstrap refresh afterward). */
export async function moveAllFailedOrdersToPending(): Promise<number> {
 const failedSnapshot = [...(await getFailedOrders())]
 let moved = 0
 for (const f of failedSnapshot) {
 const r = await moveFailedOrderToPending(f.order.client_uuid)
 if (r.ok) moved += 1
 }
 return moved
}

function normalizeClientUuid(value: string | null | undefined): string {
 return String(value ?? '').trim().toLowerCase()
}

/** Pure helper: list after removing failed entries whose client_uuid matches the set (case-insensitive). Exported for tests. */
export function computeFailedOrdersAfterRemove(list: FailedOfflineOrder[], uuids: Set<string>): FailedOfflineOrder[] {
 const removeSet = new Set(
 Array.from(uuids, (u) => normalizeClientUuid(u)).filter((u) => u.length > 0),
 )
 if (removeSet.size === 0) return list
 return list.filter((f) => !removeSet.has(normalizeClientUuid(f.order.client_uuid)))
}

export async function removeFailedByClientUuids(uuids: Set<string>): Promise<void> {
 if (uuids.size === 0) return
 const resolved = resolveOfflineScope()
 let changed = false
 await withPosOrderQueueLock(async () => {
  const list = await readFailedOrdersUnlocked(resolved)
  const next = computeFailedOrdersAfterRemove(list, uuids)
  if (next.length === list.length) return
  changed = true
  await writeFailedOrdersUnlocked(next, resolved)
 })
 if (!changed) return
 if (typeof window !== 'undefined') {
 window.dispatchEvent(new CustomEvent('pos:failed-orders-updated'))
 }
}

export async function getFailedCount(): Promise<number> {
 const list = await getFailedOrders()
 return list.length
}

export async function getLocallyOccupiedTables(): Promise<string[]> {
 const raw = await idb.getItem(POS_KEYS.locallyOccupiedTables)
 if (raw && Array.isArray(raw)) return raw as string[]
 return []
}

export async function markTableLocallyOccupied(tableId: string): Promise<void> {
 const arr = await getLocallyOccupiedTables()
 if (!arr.includes(tableId)) {
 arr.push(tableId)
 await idb.setItem(POS_KEYS.locallyOccupiedTables, arr)
 }
}

export async function markTableLocallyAvailable(tableId: string): Promise<void> {
 const arr = await getLocallyOccupiedTables()
 const filtered = arr.filter((id) => id !== tableId)
 await idb.setItem(POS_KEYS.locallyOccupiedTables, filtered)
}

export async function getCachedActiveShift<T = unknown>(
 branchId?: string | null,
 scope: OfflineCacheScope = {}
): Promise<T | null> {
 const resolved = resolveOfflineScope({ ...scope, branchId: branchId ?? scope.branchId })
 const scoped = (await idb.getItem(scopedPosKey(POS_KEYS.activeShift, resolved, 'userBranch'))) as T | null
 if (scoped && activeShiftMatchesScope(scoped, resolved)) return scoped

 const legacy = (await idb.getItem(POS_KEYS.activeShift)) as T | null
 return activeShiftMatchesScope(legacy, resolved) ? legacy : null
}

/**
 * Historical compatibility hook. A queued sale's missing Shift must not be
 * replaced with the Shift current at retry time.
 */
export async function backfillShiftIdForSync(
 order: PendingOfflineOrder,
 _branchId?: string | null,
 _resolveOpts?: Omit<ResolveShiftIdOptions, 'branchId'>,
): Promise<PendingOfflineOrder> {
 return order
}

function resolveSelectedBranchIdForBroadcast(): string | null {
 if (typeof localStorage === 'undefined') return null
 const selected = localStorage.getItem('selected_branch_id')
 if (selected) return selected
 const raw = localStorage.getItem('authUser')
 if (!raw) return null
 try {
 const parsed = JSON.parse(raw) as { user?: { current_branch_id?: string | null } }
 const id = parsed.user?.current_branch_id
 return id != null && String(id).trim() !== '' ? String(id) : null
 } catch {
 return null
 }
}

export async function setCachedActiveShift<T = unknown>(
 shift: T | null,
 scope: OfflineCacheScope = {}
): Promise<void> {
 const rec = shift && typeof shift === 'object'
 ? shift as { branch_id?: string | null }
 : null
 const resolved = resolveOfflineScope({
 ...scope,
 branchId: scope.branchId ?? rec?.branch_id,
 })
 if (shift == null) {
 await idb.removeItem(POS_KEYS.activeShift)
 await idb.removeItem(scopedPosKey(POS_KEYS.activeShift, resolved, 'userBranch'))
 } else {
 await idb.setItem(POS_KEYS.activeShift, shift)
 await idb.setItem(scopedPosKey(POS_KEYS.activeShift, resolved, 'userBranch'), shift)
 }
 if (typeof window !== 'undefined') {
 const cacheBranchId =
 resolved.branchId !== UNKNOWN_SCOPE ? String(resolved.branchId) : null
 const selectedBranchId = resolveSelectedBranchIdForBroadcast()
 const shouldBroadcast =
 !selectedBranchId ||
 !cacheBranchId ||
 String(cacheBranchId) === String(selectedBranchId)
 if (shouldBroadcast) {
 window.dispatchEvent(
 new CustomEvent('shift:cache-updated', { detail: { shift, branchId: cacheBranchId } }),
 )
 }
 }
}
